import React, {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import {
	Drawer,
	Form,
	Input,
	Table,
	Button,
	Empty,
	Tabs,
	Typography,
	ColorPicker,
	Select,
} from 'antd';
import {
	PlusOutlined,
	DeleteOutlined,
	AlignLeftOutlined,
	AlignCenterOutlined,
	AlignRightOutlined,
	VerticalAlignTopOutlined,
	VerticalAlignMiddleOutlined,
	VerticalAlignBottomOutlined,
} from '@ant-design/icons';
import {getElementRef, getViewElement} from '@core_x6/lib/util/ModelUtil';
import {getPropertyValues, toModdleValue} from '@core_x6/lib/util/PropertyUtil';
import {getLabel} from '@core_x6/lib/features/label-editing/LabelUtil';
import {toHex} from '@core_x6/lib/util/ColorUtil';
import {
	DEFAULT_STICKER_COLOR,
	isCanvasOnlyNodeType,
	STICKER,
} from '@core_x6/lib/util/CanvasNodeTypes';
import './PropertiesModal.css';

function getPropertyDefinitionLabel(def) {
	if (!def) return '—';
	const name = typeof def.name === 'string' ? def.name : def.name?.value;
	return (name && name.trim()) || def.id || '—';
}

function getDocValue(elementRef) {
	if (!elementRef?.documentation) return '';
	if (typeof elementRef.documentation === 'string') return elementRef.documentation;
	if (elementRef.documentation.value != null) return String(elementRef.documentation.value);
	if (Array.isArray(elementRef.documentation)) {
		return elementRef.documentation
			.map(d => d.value)
			.filter(Boolean)
			.join('\n');
	}
	return '';
}

function getNameValue(elementRef) {
	if (!elementRef) return '';
	const n = elementRef.name;
	return typeof n === 'string' ? n : (n?.value ?? '');
}

function getConceptId(concept) {
	if (!concept) return null;
	return typeof concept === 'string' ? concept : (concept?.id ?? concept?.$attrs?.id ?? null);
}

/** Отображаемая подпись типа элемента (например ApplicationComponent → Application Component). */
function getElementTypeLabel(type) {
	if (type == null || type === '') return '—';
	const raw = typeof type === 'string' ? type : (type?.value ?? String(type));
	const withoutNs = raw.replace(/^archimate:/i, '').trim();
	if (!withoutNs) return '—';
	return withoutNs.replace(/([A-Z])/g, ' $1').trim();
}

function getConceptDisplayName(concept, model) {
	if (!concept) return '—';
	let obj = concept;
	if (typeof concept === 'string' && model) {
		const elements = model.elementsNode?.element ?? model.elementsNode?.elements ?? [];
		const list = Array.isArray(elements) ? elements : [elements].filter(Boolean);
		obj = list.find(e => (e?.id ?? e?.$attrs?.id) === concept) ?? concept;
	}
	return (
		getNameValue(obj) ||
		getElementTypeLabel(obj?.type) ||
		(typeof concept === 'string' ? concept : '—')
	);
}

function getCanvasOnlyTypeLabel(type) {
	const base = String(type ?? '')
		.split(':')
		.pop();
	if (base === STICKER) return 'Стикер';
	return getElementTypeLabel(base);
}

function getViewNodeType(viewElement) {
	return viewElement?.type ?? viewElement?.$type ?? '';
}

/**
 * Подзаголовок для заголовка drawer: «Имя (Тип)» для node, «Имя или тип (От — К)» для edge.
 * @param {{ businessObject: object }} element — элемент с businessObject = viewElement
 * @param {object} elementRef — разрешённый концепт (elementRef или relationshipRef)
 * @param {object} [model] — модель (для разрешения source/target по id у связи)
 */

function getDrawerTitleSubtitle(element, elementRef, model) {
	const viewElement = element?.businessObject;
	if (!viewElement) return '';
	const viewType = getViewNodeType(viewElement);
	if (isCanvasOnlyNodeType(viewType)) {
		const typeLabel = getCanvasOnlyTypeLabel(viewType);
		const text = typeof viewElement.label === 'string' ? viewElement.label.trim() : '';
		return `«${text || typeLabel} (${typeLabel})»`;
	}
	if (!elementRef || typeof elementRef !== 'object') return '';
	const isConnection =
		viewElement?.$type === 'archimate:Connection' ||
		(viewElement?.relationshipRef != null && viewElement?.elementRef == null);
	if (isConnection) {
		const name = getNameValue(elementRef) || getElementTypeLabel(elementRef?.type) || '—';
		const sourceName = getConceptDisplayName(elementRef?.source, model);
		const targetName = getConceptDisplayName(elementRef?.target, model);
		return `«${name} (${sourceName} — ${targetName})»`;
	}
	const name = getNameValue(elementRef) || getElementTypeLabel(elementRef?.type) || '—';
	const typeLabel = getElementTypeLabel(elementRef?.type) || '—';
	return `«${name} (${typeLabel})»`;
}

/** Разрешает elementRef/relationshipRef (строка id) в объект концепта по модели (для X6 и др.). */
function resolveConceptRef(model, viewElement, rawRef) {
	if (!rawRef || typeof rawRef !== 'string') return rawRef;
	const isConnection =
		viewElement?.$type === 'archimate:Connection' ||
		(viewElement?.relationshipRef && !viewElement?.elementRef);
	if (isConnection) {
		const rels =
			model?.relationshipsNode?.relationship ?? model?.relationships?.relationship ?? [];
		const list = Array.isArray(rels) ? rels : [rels].filter(Boolean);
		return list.find(r => (r?.id ?? r?.$attrs?.id) === rawRef) ?? rawRef;
	}
	const elements =
		model?.elementsNode?.element ?? model?.elementsNode?.elements ?? model?.elements ?? [];
	const list = Array.isArray(elements) ? elements : [elements].filter(Boolean);
	return list.find(e => (e?.id ?? e?.$attrs?.id) === rawRef) ?? rawRef;
}

function getViewsUsingElement(model, element) {
	if (!model?.views?.diagrams?.viewsList || !element) return [];
	const concept = getElementRef(element);
	const conceptId = getConceptId(concept);
	if (!conceptId) return [];

	const viewElement = getViewElement(element);
	const isConnection = viewElement?.$type === 'archimate:Connection';

	function collectAllNodesAndConnections(parent, nodes, conns) {
		if (!parent?.viewElements) return;
		for (const ve of parent.viewElements) {
			if (ve?.$type === 'archimate:Node') {
				nodes.push(ve);
				collectAllNodesAndConnections(ve, nodes, conns);
			} else if (ve?.$type === 'archimate:Connection') {
				conns.push(ve);
			}
		}
	}

	const result = [];
	for (const view of model.views.diagrams.viewsList) {
		const nodes = [];
		const conns = [];
		collectAllNodesAndConnections(view, nodes, conns);
		let used = false;
		if (isConnection) {
			used = conns.some(c => getConceptId(c?.relationshipRef) === conceptId);
		} else {
			used = nodes.some(n => getConceptId(n?.elementRef) === conceptId);
		}
		if (used) {
			result.push({
				id: view.id,
				name: view.name || view.id || '—',
			});
		}
	}
	return result;
}

function getConnectionsOfElement(element) {
	if (!element) return [];
	const incoming = element.incoming || [];
	const outgoing = element.outgoing || [];
	const seen = new Set();
	const list = [];

	function add(conn) {
		const id = conn.id;
		if (seen.has(id)) return;
		seen.add(id);
		const sourceName = conn.source
			? getLabel(conn.source) || getNameValue(conn.source) || '—'
			: '—';
		const targetName = conn.target
			? getLabel(conn.target) || getNameValue(conn.target) || '—'
			: '—';
		const typeName = conn.type || conn.businessObject?.relationshipRef?.type || '—';
		const shortType =
			typeName.replace(/^archimate:/, '').replace(/Relationship$/, '') || typeName;
		list.push({
			id,
			type: shortType,
			source: sourceName,
			target: targetName,
		});
	}

	incoming.forEach(c => add(c));
	outgoing.forEach(c => add(c));
	return list;
}

const TEXT_ALIGN_H_ICONS = [
	{value: 'left', Icon: AlignLeftOutlined, title: 'Лево'},
	{value: 'center', Icon: AlignCenterOutlined, title: 'Центр'},
	{value: 'right', Icon: AlignRightOutlined, title: 'Право'},
];
const TEXT_ALIGN_V_ICONS = [
	{value: 'top', Icon: VerticalAlignTopOutlined, title: 'Верх'},
	{value: 'middle', Icon: VerticalAlignMiddleOutlined, title: 'Центр'},
	{value: 'bottom', Icon: VerticalAlignBottomOutlined, title: 'Низ'},
];

function getTabList(modeler, elementRef, {isSticker} = {}) {
	const hasAppearance =
		typeof modeler?.get?.('modeling')?.updateViewElementAppearance === 'function';
	if (isSticker) {
		return [
			{key: 'main', tab: 'Основное'},
			...(hasAppearance ? [{key: 'appearance', tab: 'Внешний вид'}] : []),
		];
	}
	const elType = elementRef?.type ?? elementRef?.$type ?? '';
	const isJunction = elType === 'AndJunction' || elType === 'OrJunction';
	const showAppearance = hasAppearance && !isJunction;
	return [
		{key: 'main', tab: 'Основное'},
		{key: 'attributes', tab: 'Атрибуты'},
		...(showAppearance ? [{key: 'appearance', tab: 'Внешний вид'}] : []),
		{key: 'diagrams', tab: 'Диаграммы'},
		{key: 'connections', tab: 'Связи'},
	];
}

function buildAttributesSignature(rows) {
	const normalized = (rows ?? []).map(r => ({
		definitionId: String(r?.definitionId ?? ''),
		definitionLabel: String(r?.definitionLabel ?? ''),
		value: String(r?.value ?? ''),
	}));
	return JSON.stringify(normalized);
}

const PropertiesModal = ({modeler, isOpen, element, onClose}) => {
	const [activeTab, setActiveTab] = useState('main');
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [attributes, setAttributes] = useState([]); // { definitionId, definitionLabel, value }
	const [size, setSize] = useState(400);
	const [appearanceFillColor, setAppearanceFillColor] = useState('');
	const [appearanceLineColor, setAppearanceLineColor] = useState('');
	const [appearanceTextAlignH, setAppearanceTextAlignH] = useState('center');
	const [appearanceTextAlignV, setAppearanceTextAlignV] = useState('top');
	const [junctionType, setJunctionType] = useState('And'); // 'And' | 'Or' — только для Junction

	const rawElementRef = element ? getElementRef(element) : null;
	const viewElement = element ? getViewElement(element) : null;
	const model = modeler?.getModel?.() ?? null;
	const elementRef =
		model && viewElement && rawElementRef
			? typeof rawElementRef === 'string'
				? resolveConceptRef(model, viewElement, rawElementRef)
				: rawElementRef
			: rawElementRef;
	const isConnection =
		viewElement?.$type === 'archimate:Connection' ||
		(viewElement?.relationshipRef != null && viewElement?.elementRef == null);
	const isSticker = !isConnection && isCanvasOnlyNodeType(getViewNodeType(viewElement));
	const propertyDefinitions = model?.propertyDefinitionsNode?.propertyDefinitions ?? [];
	const modeling = modeler?.get?.('modeling');
	const moddle = modeler?.get?.('moddle');
	const lastSavedRef = useRef({
		name: '',
		description: '',
		attributesSignature: '[]',
	});

	const analysisData = useMemo(
		() => ({
			views: model && element ? getViewsUsingElement(model, element) : [],
			connections:
				elementRef && typeof elementRef === 'object'
					? getConnectionsOfElement(elementRef)
					: [],
		}),
		[model, element, elementRef, isOpen]
	);

	// У Junction нет вкладки «Внешний вид» — переключаем на «Основное», если она была открыта
	const elTypeForTabs = elementRef?.type ?? elementRef?.$type ?? '';
	const isJunctionForTabs = elTypeForTabs === 'AndJunction' || elTypeForTabs === 'OrJunction';
	useEffect(() => {
		if (isJunctionForTabs && activeTab === 'appearance') setActiveTab('main');
		if (
			isSticker &&
			(activeTab === 'attributes' || activeTab === 'diagrams' || activeTab === 'connections')
		) {
			setActiveTab('main');
		}
	}, [isJunctionForTabs, isSticker, activeTab]);

	// Sync from element when modal opens or element changes
	useEffect(() => {
		if (!isOpen) return;
		if (isSticker) {
			const labelVal = viewElement?.label != null ? String(viewElement.label) : '';
			setName(labelVal);
			setDescription('');
			setAttributes([]);
			lastSavedRef.current = {
				name: labelVal,
				description: '',
				attributesSignature: '[]',
			};
			return;
		}
		if (!elementRef) return;
		const defs = model?.propertyDefinitionsNode?.propertyDefinitions ?? [];
		// Для связи: имя из relationshipRef или из viewElement.label (подпись на ребре)
		const nameVal =
			getNameValue(elementRef) ||
			(isConnection && viewElement?.label != null && viewElement.label !== ''
				? String(viewElement.label)
				: '');
		setName(nameVal);
		setDescription(getDocValue(elementRef));
		const elType = elementRef?.type ?? elementRef?.$type ?? '';
		if (elType === 'AndJunction' || elType === 'OrJunction') {
			setJunctionType(elType === 'OrJunction' ? 'Or' : 'And');
		}
		const propsContainer = elementRef.properties;
		const list = propsContainer?.property ?? [];
		const rows = list.map(pr => {
			const ref = pr.propertyDefinitionRef;
			const defId = ref && (typeof ref === 'object' ? ref.id : ref);
			const def = defs.find(d => d.id === defId);
			const refName = ref && typeof ref === 'object' ? (ref.name?.value ?? ref.name) : null;
			const label = def ? getPropertyDefinitionLabel(def) : refName || defId || '—';
			const values = defId ? getPropertyValues(elementRef, defId) : [];
			return {
				definitionId: defId,
				definitionLabel: label,
				value: values.length > 0 ? values.join(', ') : '',
			};
		});
		setAttributes(rows);
		lastSavedRef.current = {
			name: nameVal,
			description: getDocValue(elementRef),
			attributesSignature: buildAttributesSignature(rows),
		};
	}, [isOpen, element?.id, elementRef?.id, elementRef?.name, isConnection, isSticker, viewElement?.label]);

	// Sync appearance from viewElement.style (X6)
	// Для Junction: в модели заливка может не храниться или быть чёрной; отображаем семантические значения (Or — белый, And — чёрный)
	useEffect(() => {
		if (!isOpen || !viewElement) return;
		const elType = elementRef?.type ?? elementRef?.$type ?? getViewNodeType(viewElement);
		const isOrJunction = elType === 'OrJunction';
		const isAndJunction = elType === 'AndJunction';
		const isStickerAppearance = isCanvasOnlyNodeType(elType);
		const style = viewElement.style;
		const fillColorObj = style?.fillColor ?? style?.get?.('fillColor');
		const lineColorObj = style?.lineColor ?? style?.get?.('lineColor');
		const fillHex =
			fillColorObj &&
			toHex({
				r: Number(fillColorObj.r ?? 255),
				g: Number(fillColorObj.g ?? 255),
				b: Number(fillColorObj.b ?? 255),
				a: fillColorObj.a != null ? Number(fillColorObj.a) : 100,
			});
		const isBlack =
			fillColorObj &&
			Number(fillColorObj.r ?? 255) === 0 &&
			Number(fillColorObj.g ?? 255) === 0 &&
			Number(fillColorObj.b ?? 255) === 0;
		let displayFill = fillHex;
		if (isOrJunction && (!displayFill || isBlack)) displayFill = '#ffffff';
		else if (isAndJunction && !displayFill) displayFill = '#000000';
		else if (!displayFill) displayFill = isStickerAppearance ? DEFAULT_STICKER_COLOR : '#ffffff';
		const lineHex =
			lineColorObj &&
			toHex({
				r: Number(lineColorObj.r ?? 0),
				g: Number(lineColorObj.g ?? 0),
				b: Number(lineColorObj.b ?? 0),
				a: lineColorObj.a != null ? Number(lineColorObj.a) : 100,
			});
		setAppearanceFillColor(displayFill);
		setAppearanceLineColor(lineHex || '#000000');
		const defaultAlign = isStickerAppearance ? 'center-middle' : 'center-top';
		const textAlign = style?.textAlign ?? style?.get?.('textAlign') ?? defaultAlign;
		const parts = String(textAlign).split('-');
		setAppearanceTextAlignH(parts[0] || 'center');
		setAppearanceTextAlignV(parts[1] || (isStickerAppearance ? 'middle' : 'top'));
	}, [isOpen, viewElement?.id, viewElement?.style, elementRef?.type, viewElement?.type]);

	const applyAppearance = useCallback(
		payload => {
			const modeling = modeler?.get?.('modeling');
			if (!modeling?.updateViewElementAppearance || !viewElement) return;
			modeling.updateViewElementAppearance(viewElement, payload);
		},
		[modeler, viewElement]
	);

	const buildPropertiesFromAttributes = rows => {
		if (!moddle || !model) return null;
		const valid = (rows ?? []).filter(r => r.definitionLabel?.trim?.() || r.definitionId);
		if (valid.length === 0) return moddle.create('archimate:Properties', {property: []});

		// ensure model has propertyDefinitionsNode
		let node = model.propertyDefinitionsNode;
		if (!node) {
			node = moddle.create('archimate:PropertyDefinitions', {propertyDefinitions: []});
			model.propertyDefinitionsNode = node;
		}
		if (!node.propertyDefinitions) node.propertyDefinitions = [];
		const defsList = node.propertyDefinitions;

		const propertyArray = valid
			.map(row => {
				const nameToUse = (row.definitionLabel || row.definitionId || '').trim();
				if (!nameToUse) return null;

				let def = defsList.find(
					d =>
						(typeof d.name === 'string' ? d.name : d.name?.value) === nameToUse ||
						d.id === row.definitionId
				);
				if (!def) {
					def = moddle.create('archimate:PropertyDefinition', {});
					if (moddle.ids?.nextPrefixed) {
						def.id = moddle.ids.nextPrefixed('prop-def-', def);
					} else {
						def.id = `prop-def-${nameToUse.replace(/\s+/g, '-')}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
					}
					if (typeof def.set === 'function') {
						def.set('name', nameToUse);
						def.set('type', 'string');
					} else {
						def.name = nameToUse;
						def.type = 'string';
					}
					defsList.push(def);
				}

				// ссылка на тот же объект PropertyDefinition из модели — в elementRef будет id, name, type
				return moddle.create('archimate:Property', {
					propertyDefinitionRef: def,
					value: toModdleValue(row.value),
				});
			})
			.filter(Boolean);

		return moddle.create('archimate:Properties', {property: propertyArray});
	};

	const commitChanges = useCallback(
		(overrides = {}) => {
			if (!element || !modeling) return;
			const nextName = overrides.name ?? name;
			if (isSticker) {
				const nextSnapshotName = nextName || '';
				if (lastSavedRef.current.name === nextSnapshotName) return;
				modeling.updateViewElementLabel?.(element, nextSnapshotName);
				lastSavedRef.current = {
					name: nextSnapshotName,
					description: '',
					attributesSignature: '[]',
				};
				return;
			}
			const nextDescription = overrides.description ?? description;
			const nextAttributes = overrides.attributes ?? attributes;
			const nextSnapshot = {
				name: nextName || '',
				description: nextDescription || '',
				attributesSignature: buildAttributesSignature(nextAttributes),
			};
			const prevSnapshot = lastSavedRef.current;
			if (
				prevSnapshot.name === nextSnapshot.name &&
				prevSnapshot.description === nextSnapshot.description &&
				prevSnapshot.attributesSignature === nextSnapshot.attributesSignature
			) {
				return;
			}

			const payload = {};
			const nameChanged = prevSnapshot.name !== nextSnapshot.name;
			const descriptionChanged = prevSnapshot.description !== nextSnapshot.description;
			const attributesChanged =
				prevSnapshot.attributesSignature !== nextSnapshot.attributesSignature;

			if (nameChanged) payload.name = nextName || undefined;
			if (descriptionChanged) payload.documentation = nextDescription || undefined;
			if (attributesChanged) {
				const newProps = moddle ? buildPropertiesFromAttributes(nextAttributes) : null;
				if (newProps) payload.properties = newProps;
			}
			if (Object.keys(payload).length === 0) return;

			modeling.updateProperties(element, payload);
			lastSavedRef.current = nextSnapshot;
		},
		[element, modeling, name, description, attributes, moddle, model, isSticker]
	);

	const addAttributeRow = () => {
		setAttributes(prev => [
			...prev,
			{
				definitionId: '',
				definitionLabel: '',
				value: '',
			},
		]);
	};

	const updateAttribute = (index, field, value) => {
		setAttributes(prev => {
			const next = [...prev];
			next[index] = {...next[index], [field]: value};
			if (field === 'definitionId') {
				const def = propertyDefinitions.find(d => d.id === value);
				next[index].definitionLabel = def ? getPropertyDefinitionLabel(def) : (value ?? '');
			}
			return next;
		});
	};

	const updateAttributeName = (index, name) => {
		setAttributes(prev => {
			const next = [...prev];
			next[index] = {...next[index], definitionId: name, definitionLabel: name};
			return next;
		});
	};

	const removeAttribute = index => {
		setAttributes(prev => {
			const next = prev.filter((_, i) => i !== index);
			commitChanges({attributes: next});
			return next;
		});
	};

	const isJunction =
		!isConnection &&
		elementRef &&
		typeof elementRef === 'object' &&
		(elementRef.type === 'AndJunction' || elementRef.type === 'OrJunction');

	const handleJunctionTypeChange = useCallback(
		value => {
			if (!element || !modeling || (value !== 'And' && value !== 'Or')) return;
			const newType = value === 'Or' ? 'OrJunction' : 'AndJunction';
			setJunctionType(value);
			modeling.updateProperties(element, {type: newType});
		},
		[element, modeling]
	);

	const contentList = {
		main: (
			<Form layout="vertical">
				{isSticker ? (
					<Form.Item label="Текст">
						<Input.TextArea
							value={name}
							onChange={e => setName(e.target.value)}
							onBlur={() => commitChanges()}
							onKeyDown={e => {
								if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
									e.preventDefault();
									commitChanges();
								}
							}}
							placeholder="Текст"
							rows={6}
						/>
					</Form.Item>
				) : (
					<>
						<Form.Item label="Имя">
							<Input
								value={name}
								onChange={e => setName(e.target.value)}
								onBlur={() => commitChanges()}
								onPressEnter={() => commitChanges()}
								placeholder="Имя элемента"
							/>
						</Form.Item>
						{isJunction && (
							<Form.Item label="Тип">
								<Select
									value={junctionType}
									onChange={handleJunctionTypeChange}
									options={[
										{value: 'And', label: 'And'},
										{value: 'Or', label: 'Or'},
									]}
									style={{width: '100%'}}
								/>
							</Form.Item>
						)}
						<Form.Item label="Описание">
							<Input.TextArea
								value={description}
								onChange={e => setDescription(e.target.value)}
								onBlur={() => commitChanges()}
								onPressEnter={() => commitChanges()}
								onKeyDown={e => {
									if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
										e.preventDefault();
										commitChanges();
									}
								}}
								placeholder="Описание"
								rows={4}
							/>
						</Form.Item>
					</>
				)}
			</Form>
		),
		appearance: (() => {
			const gridStyle = {
				display: 'grid',
				gridTemplateColumns: 'auto 1fr',
				alignItems: 'center',
				gap: '10px 16px',
				maxWidth: 480,
			};
			const iconBtnStyle = selected => ({
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				width: 28,
				height: 28,
				borderRadius: 4,
				cursor: 'pointer',
				background: selected ? 'rgba(22, 119, 255, 0.15)' : 'transparent',
				color: selected ? '#1677ff' : 'rgba(0,0,0,0.65)',
			});
			const handleColorChange = field => (color, hexOrCss) => {
				const hex = color?.toHexString?.() ?? hexOrCss ?? '';
				if (field === 'fill') {
					setAppearanceFillColor(hex);
					applyAppearance({fillColor: hex});
				} else {
					setAppearanceLineColor(hex);
					applyAppearance({lineColor: hex});
				}
			};
			const controlWrap = {justifySelf: 'start', width: 'fit-content'};
			const renderAlignIcons = (icons, selected, onSelect) =>
				icons.map(({value, Icon, title}) => (
					<span
						key={value}
						role="button"
						title={title}
						tabIndex={0}
						onClick={() => onSelect(value)}
						onKeyDown={e => e.key === 'Enter' && onSelect(value)}
						style={iconBtnStyle(selected === value)}
					>
						<Icon style={{fontSize: 16}} />
					</span>
				));
			const applyH = value => {
				setAppearanceTextAlignH(value);
				applyAppearance({textAlign: `${value}-${appearanceTextAlignV}`});
			};
			const applyV = value => {
				setAppearanceTextAlignV(value);
				applyAppearance({textAlign: `${appearanceTextAlignH}-${value}`});
			};
			if (isSticker) {
				return (
					<div style={gridStyle}>
						<span>Заливка:</span>
						<div style={controlWrap}>
							<ColorPicker
								value={appearanceFillColor}
								onChange={handleColorChange('fill')}
								showText
							/>
						</div>
						<span>Выравнивание текста:</span>
						<div style={{...controlWrap, display: 'flex', alignItems: 'center', gap: 6}}>
							{renderAlignIcons(TEXT_ALIGN_H_ICONS, appearanceTextAlignH, applyH)}
						</div>
						<span>Выравнивание текста:</span>
						<div style={{...controlWrap, display: 'flex', alignItems: 'center', gap: 6}}>
							{renderAlignIcons(TEXT_ALIGN_V_ICONS, appearanceTextAlignV, applyV)}
						</div>
					</div>
				);
			}
			return (
				<div style={gridStyle}>
					{!isConnection && (
						<>
							<span>Заливка:</span>
							<div style={controlWrap}>
								<ColorPicker
									value={appearanceFillColor}
									onChange={handleColorChange('fill')}
									showText
								/>
							</div>
							<div
								style={{
									gridColumn: '1 / -1',
									display: 'flex',
									alignItems: 'center',
									gap: 24,
									flexWrap: 'nowrap',
								}}
							>
								<div style={{display: 'flex', alignItems: 'center', gap: 6}}>
									<span style={{flexShrink: 0}}>Выравнивание текста:</span>
									{renderAlignIcons(
										TEXT_ALIGN_H_ICONS,
										appearanceTextAlignH,
										applyH
									)}
								</div>
								<div style={{display: 'flex', alignItems: 'center', gap: 6}}>
									<span style={{flexShrink: 0}}>Позиция текста:</span>
									{renderAlignIcons(
										TEXT_ALIGN_V_ICONS,
										appearanceTextAlignV,
										applyV
									)}
								</div>
							</div>
						</>
					)}
					<span>Цвет линии:</span>
					<div style={controlWrap}>
						<ColorPicker
							value={appearanceLineColor}
							onChange={handleColorChange('line')}
							showText
						/>
					</div>
				</div>
			);
		})(),
		attributes: (
			<div>
				<div style={{marginBottom: 8}}>
					<Button type="primary" icon={<PlusOutlined />} onClick={addAttributeRow}>
						Добавить
					</Button>
				</div>
				<Table
					dataSource={attributes}
					rowKey={(_, i) => String(i)}
					pagination={false}
					size="small"
					bordered
					locale={{
						emptyText: (
							<Empty description="У элемента нет атрибутов" style={{marginTop: 16}} />
						),
					}}
					columns={[
						{
							title: 'Наименование',
							dataIndex: 'definitionLabel',
							width: '40%',
							render: (_, record, index) => (
								<Input
									value={record.definitionLabel ?? record.definitionId ?? ''}
									onChange={e => updateAttributeName(index, e.target.value)}
									onBlur={() => commitChanges()}
									onPressEnter={() => commitChanges()}
									placeholder="Введите наименование"
								/>
							),
						},
						{
							title: 'Значение',
							dataIndex: 'value',
							render: (_, record, index) => (
								<Input
									value={record.value}
									onChange={e => updateAttribute(index, 'value', e.target.value)}
									onBlur={() => commitChanges()}
									onPressEnter={() => commitChanges()}
									placeholder="Значение"
								/>
							),
						},
						{
							title: '',
							width: 56,
							render: (_, __, index) => (
								<Button
									type="text"
									danger
									icon={<DeleteOutlined />}
									onClick={() => removeAttribute(index)}
								/>
							),
						},
					]}
				/>
			</div>
		),
		diagrams: (
			<div style={{padding: '0 4px'}}>
				<Typography.Title level={5} style={{marginTop: 0}}>
					Диаграммы, в которых используется элемент
				</Typography.Title>
				<Table
					dataSource={analysisData.views}
					rowKey="id"
					pagination={false}
					size="small"
					bordered
					locale={{
						emptyText: (
							<Empty
								description="Элемент не найден в других диаграммах"
								style={{margin: 16}}
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						),
					}}
					columns={[
						{
							title: 'Диаграмма',
							dataIndex: 'name',
						},
					]}
				/>
			</div>
		),
		connections: (
			<div style={{padding: '0 4px'}}>
				<Typography.Title level={5} style={{marginTop: 0}}>
					Связи элемента
				</Typography.Title>
				<Table
					dataSource={analysisData.connections}
					rowKey="id"
					pagination={false}
					size="small"
					bordered
					locale={{
						emptyText: (
							<Empty
								description="Нет входящих и исходящих связей"
								style={{margin: 16}}
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						),
					}}
					onRow={record => ({
						onClick: () => {
							const elRegistry = modeler?.get?.('elementRegistry');
							const sel = modeler?.get?.('selection');
							if (!elRegistry || !sel) return;
							const conn = elRegistry.get(record.id);
							if (conn) sel.select(conn);
						},
						style: {cursor: 'pointer'},
					})}
					columns={[
						{
							title: 'Тип',
							dataIndex: 'type',
							width: 120,
						},
						{
							title: 'Источник',
							dataIndex: 'source',
						},
						{
							title: 'Цель',
							dataIndex: 'target',
						},
					]}
				/>
			</div>
		),
	};

	if (!element) return null;

	const drawerSubtitle = getDrawerTitleSubtitle(element, elementRef, model);
	const drawerTitle = drawerSubtitle
		? `Свойства элемента ${drawerSubtitle}`
		: 'Свойства элемента';

	return (
		<Drawer
			title={drawerTitle}
			open={isOpen}
			onClose={() => {
				commitChanges();
				onClose?.();
			}}
			placement="bottom"
			size={size}
			styles={{
				body: {
					padding: 0,
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
			resizable={{
				onResize: newSize => setSize(newSize),
			}}
			mask={false}
		>
			{!elementRef && !isSticker ? (
				<Empty description="Нет данных элемента" />
			) : (
				<Tabs
					className="properties-modal-tabs"
					activeKey={activeTab}
					onChange={setActiveTab}
					tabPlacement="left"
					styles={{content: {padding: '24px'}}}
					items={getTabList(modeler, elementRef, {isSticker}).map(({key, tab}) => ({
						key,
						label: tab,
						children: contentList[key],
					}))}
				/>
			)}
		</Drawer>
	);
};

export default PropertiesModal;
